// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Versions Tab
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { C, formatDate } from '../../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge } from '../../../../../lib/ui';
import { VersionDiff } from './VersionDiff';
import type { EstadoDocumento, DocumentVersion } from '../../../../../types';

export interface VersionsTabProps {
  versions: DocumentVersion[];
  selectedVersionIndex: number;
  onSelectVersion: (index: number) => void;
  versionVigenteId?: string | null;
}

export function VersionsTab({
  versions,
  selectedVersionIndex,
  onSelectVersion,
  versionVigenteId,
}: VersionsTabProps) {
  const [diffVersionId, setDiffVersionId] = useState<string | null>(null);

  // The latest version (first in the list, sorted desc by numeroEdicion) is the "current" one
  const currentVersion = versions[0] ?? null;

  const handleCompare = useCallback((versionId: string) => {
    setDiffVersionId(versionId);
  }, []);

  const handleCloseDiff = useCallback(() => {
    setDiffVersionId(null);
  }, []);

  // If diff mode is active, find the selected version and render VersionDiff
  if (diffVersionId && currentVersion) {
    const olderVersion = versions.find((v) => v.id === diffVersionId);
    if (olderVersion) {
      return (
        <VersionDiff versionA={olderVersion} versionB={currentVersion} onClose={handleCloseDiff} />
      );
    }
  }

  return (
    <div style={{ background: '#ECEEF2', padding: '24px 28px', minHeight: 'calc(100vh - 200px)' }}>
      <div
        style={{
          maxWidth: 680,
          margin: '0 auto',
          background: 'white',
          borderRadius: 10,
          padding: '24px 28px',
        }}
      >
        <h2
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: C.azul1,
            fontFamily: "'Source Serif 4',Georgia,serif",
            margin: '0 0 20px',
          }}
        >
          Historial de Versiones
        </h2>
        {versions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: C.textMut, fontSize: 13 }}>
            Sin versiones disponibles
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {versions.map((v, idx) => {
              const isViewing = idx === selectedVersionIndex;
              return (
                <div
                  key={v.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: isViewing ? '8px 12px' : undefined,
                    background: isViewing ? '#F0F9FF' : undefined,
                    borderRadius: isViewing ? 8 : undefined,
                    border: isViewing ? `1px solid ${C.softBlue}` : '1px solid transparent',
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: isViewing
                        ? 'linear-gradient(135deg, #1F3864, #2F5496)'
                        : 'linear-gradient(135deg, #1A94BF, #33BCE1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    v{v.numeroEdicion}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                      Edicion {String(v.numeroEdicion).padStart(2, '0')}
                      {isViewing && (
                        <span
                          style={{ fontSize: 11, color: C.azul2, fontWeight: 500, marginLeft: 8 }}
                        >
                          (visualizando)
                        </span>
                      )}
                    </div>
                    {/* 0665: prioriza asunto (cross-artefacto canónico),
                        fallback a descripcionCambios legacy. Si hay alcance, lo
                        muestra como subtexto truncado. */}
                    {(v.asunto || v.descripcionCambios) && (
                      <div
                        style={{
                          fontSize: 12,
                          color: C.textSec,
                          marginTop: 2,
                          fontWeight: v.asunto ? 600 : 400,
                        }}
                      >
                        {v.asunto ?? v.descripcionCambios}
                      </div>
                    )}
                    {v.alcance && v.asunto && (
                      <div
                        style={{
                          fontSize: 11,
                          color: C.textMut,
                          marginTop: 2,
                          lineHeight: 1.4,
                        }}
                      >
                        {v.alcance.length > 140 ? `${v.alcance.slice(0, 140)}…` : v.alcance}
                      </div>
                    )}
                    {v.autor && (
                      <div style={{ fontSize: 11, color: C.textMut, marginTop: 2 }}>
                        {v.autor.nombreCompleto}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {/* "Ver esta version" button */}
                    {!isViewing && (
                      <button
                        onClick={() => onSelectVersion(idx)}
                        title={`Ver Edicion ${String(v.numeroEdicion).padStart(2, '0')}`}
                        style={{
                          background: 'none',
                          border: `1px solid ${C.border}`,
                          borderRadius: 6,
                          color: C.azul2,
                          cursor: 'pointer',
                          padding: '4px 10px',
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = C.azul2;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.borderColor = C.azul2;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.color = C.azul2;
                          e.currentTarget.style.borderColor = C.border;
                        }}
                      >
                        <Ic d={ICON_PATHS.eye} s={12} c="currentColor" />
                        Ver
                      </button>
                    )}
                    {/* Show "Comparar" button for all versions except the current (first) one */}
                    {idx > 0 && versions.length > 1 && (
                      <button
                        onClick={() => handleCompare(v.id)}
                        title={`Comparar Edicion ${String(v.numeroEdicion).padStart(2, '0')} con la edicion actual`}
                        style={{
                          background: 'none',
                          border: `1px solid ${C.border}`,
                          borderRadius: 6,
                          color: C.azulClaro,
                          cursor: 'pointer',
                          padding: '4px 10px',
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = C.azulClaro;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.borderColor = C.azulClaro;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.color = C.azulClaro;
                          e.currentTarget.style.borderColor = C.border;
                        }}
                      >
                        Comparar
                      </button>
                    )}
                    <div style={{ textAlign: 'right' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <StatusBadge
                          status={
                            v.id === versionVigenteId
                              ? ('PUBLICADO' as EstadoDocumento)
                              : (v.estado as EstadoDocumento)
                          }
                          small
                        />
                        {v.firmado && (
                          <span
                            title={
                              v.fechaFirma
                                ? `Firmado el ${formatDate(v.fechaFirma)}`
                                : 'Publicado con firma electrónica'
                            }
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: '#1A94BF',
                              padding: '1px 5px',
                              borderRadius: 3,
                              border: '1px solid #1A94BF',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                              <path d="M9 12l2 2 4-4" />
                            </svg>
                            Firmado
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMut, marginTop: 4 }}>
                        {formatDate(v.fechaAprobacion ?? v.creadoEn)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
